import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon, Message } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import {
  getAutoLeaveSettings,
  getCompanyLeaveTypes,
  saveAutoLeaveSettings,
} from "./autoLeaveApi";
import "../general/General.css";
import "./AutoLeaveDeduction.css";

const defaultSettings = {
  isEnabled: false,
  deductOnMissingCheckIn: false,
  deductOnMissingTimesheet: false,
  primaryLeaveTypeId: null,
  createUnpaidWhenInsufficientBalance: false,
  unpaidLeaveTypeId: null,
  autoApprove: false,
  notifyEmployee: true,
  notifyManager: true,
  skipHolidaysAndNonWorkingDays: true,
  skipIfAlreadyOnLeave: true,
};

const AUTO_SAVE_DELAY = 600;

const toSavePayload = (settings) => ({
  ...settings,
  primaryLeaveTypeId: settings.primaryLeaveTypeId
    ? Number(settings.primaryLeaveTypeId)
    : null,
  unpaidLeaveTypeId:
    settings.createUnpaidWhenInsufficientBalance && settings.unpaidLeaveTypeId
      ? Number(settings.unpaidLeaveTypeId)
      : null,
});

const ToggleRow = ({ title, description, checked, onChange, disabled }) => (
  <div className={`privacy-card ${disabled ? "auto-leave-card--disabled" : ""}`}>
    <div className="privacy-content">
      <div className="privacy-info">
        <div className="privacy-header">
          <h4 className="privacy-title">{title}</h4>
        </div>
        {description ? <p className="privacy-description">{description}</p> : null}
      </div>
      <div className="privacy-control">
        <div className="toggle-switch-container">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={!!checked}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="toggle-slider" />
          </label>
        </div>
      </div>
    </div>
  </div>
);

const AutoLeaveDeductionSettings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { isSuspended } = useSubscriptionStatus();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [settings, setSettings] = useState(defaultSettings);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const hasUserEdited = useRef(false);
  const saveQueue = useRef(Promise.resolve());
  const saveVersion = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [settingsData, types] = await Promise.all([
        getAutoLeaveSettings(),
        getCompanyLeaveTypes(),
      ]);
      hasUserEdited.current = false;
      setSettings({
        ...defaultSettings,
        ...(settingsData || {}),
        primaryLeaveTypeId: settingsData?.primaryLeaveTypeId ?? null,
        unpaidLeaveTypeId: settingsData?.unpaidLeaveTypeId ?? null,
      });
      setLeaveTypes((types || []).filter((lt) => lt.active !== false));
    } catch (err) {
      setError(err?.message || t("settings.autoLeave.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isSuspended) {
      load();
    }
  }, [isSuspended, load]);

  const activeLeaveTypes = useMemo(
    () => leaveTypes.filter((lt) => lt.active !== false),
    [leaveTypes]
  );

  const setField = (key, value) => {
    hasUserEdited.current = true;
    setSuccess("");
    setSettings((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "createUnpaidWhenInsufficientBalance" && !value
        ? { unpaidLeaveTypeId: null }
        : {}),
    }));
  };

  const validate = useCallback((candidate) => {
    if (!candidate.isEnabled) return null;
    if (!candidate.deductOnMissingCheckIn && !candidate.deductOnMissingTimesheet) {
      return t("settings.autoLeave.validation.triggerRequired");
    }
    if (!candidate.primaryLeaveTypeId) {
      return t("settings.autoLeave.validation.primaryRequired");
    }
    if (candidate.createUnpaidWhenInsufficientBalance && !candidate.unpaidLeaveTypeId) {
      return t("settings.autoLeave.validation.unpaidRequired");
    }
    if (
      candidate.createUnpaidWhenInsufficientBalance &&
      candidate.unpaidLeaveTypeId &&
      Number(candidate.unpaidLeaveTypeId) === Number(candidate.primaryLeaveTypeId)
    ) {
      return t("settings.autoLeave.validation.unpaidDifferent");
    }
    return null;
  }, [t]);

  useEffect(() => {
    if (loading || !hasUserEdited.current) {
      return undefined;
    }

    const version = saveVersion.current + 1;
    saveVersion.current = version;
    const validationError = validate(settings);

    if (validationError) {
      setError(validationError);
      setSuccess("");
      return undefined;
    }

    setError("");
    setSuccess("");
    const payload = toSavePayload(settings);
    const timer = setTimeout(() => {
      const request = saveQueue.current
        .catch(() => undefined)
        .then(() => saveAutoLeaveSettings(payload));

      saveQueue.current = request;
      request
        .then(() => {
          if (isMounted.current && version === saveVersion.current) {
            setSuccess(t("settings.autoLeave.saveSuccess"));
          }
        })
        .catch((err) => {
          if (isMounted.current && version === saveVersion.current) {
            setError(err?.message || t("settings.autoLeave.saveError"));
          }
        });
    }, AUTO_SAVE_DELAY);

    return () => clearTimeout(timer);
  }, [loading, settings, t, validate]);

  if (isSuspended) {
    return null;
  }

  return (
    <div className="workspace-settings">
      <div className="settings-section auto-leave-settings privacy-settings">
        <div className="section-header">
          <div className="section-icon privacy">
            <Icon name="calendar minus outline" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("settings.autoLeave.title")}</h3>
            <p className="section-description">{t("settings.autoLeave.description")}</p>
          </div>
        </div>

      {loading ? (
        <Message info content={t("settings.autoLeave.loading")} />
      ) : (
        <>
          {error ? <Message negative content={error} onDismiss={() => setError("")} /> : null}
          {success ? <Message positive content={success} onDismiss={() => setSuccess("")} /> : null}

          <div className="auto-leave-section">
            <h4 className="auto-leave-section-title">{t("settings.autoLeave.masterTitle")}</h4>
            <ToggleRow
              title={t("settings.autoLeave.enable")}
              description={t("settings.autoLeave.enableDescription")}
              checked={settings.isEnabled}
              onChange={(v) => setField("isEnabled", v)}
            />
          </div>

          <div className={`auto-leave-section ${!settings.isEnabled ? "auto-leave-section--muted" : ""}`}>
            <h4 className="auto-leave-section-title">{t("settings.autoLeave.triggersTitle")}</h4>
            <ToggleRow
              title={t("settings.autoLeave.missingCheckIn")}
              description={t("settings.autoLeave.missingCheckInDescription")}
              checked={settings.deductOnMissingCheckIn}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("deductOnMissingCheckIn", v)}
            />
            <ToggleRow
              title={t("settings.autoLeave.missingTimesheet")}
              description={t("settings.autoLeave.missingTimesheetDescription")}
              checked={settings.deductOnMissingTimesheet}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("deductOnMissingTimesheet", v)}
            />
          </div>

          <div className={`auto-leave-section ${!settings.isEnabled ? "auto-leave-section--muted" : ""}`}>
            <h4 className="auto-leave-section-title">{t("settings.autoLeave.leaveImpactTitle")}</h4>
            <div className="privacy-card">
              <div className="privacy-content auto-leave-select-row">
                <div className="privacy-info">
                  <h4 className="privacy-title">{t("settings.autoLeave.primaryLeaveType")}</h4>
                  <p className="privacy-description">
                    {t("settings.autoLeave.primaryLeaveTypeDescription")}
                  </p>
                </div>
                <select
                  className="auto-leave-select"
                  disabled={!settings.isEnabled}
                  value={settings.primaryLeaveTypeId ?? ""}
                  onChange={(e) =>
                    setField("primaryLeaveTypeId", e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">{t("settings.autoLeave.selectLeaveType")}</option>
                  {activeLeaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                      {lt.deducted === false ? ` (${t("settings.autoLeave.unpaidLabel")})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <ToggleRow
              title={t("settings.autoLeave.unpaidFallback")}
              description={t("settings.autoLeave.unpaidFallbackDescription")}
              checked={settings.createUnpaidWhenInsufficientBalance}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("createUnpaidWhenInsufficientBalance", v)}
            />

            {settings.createUnpaidWhenInsufficientBalance ? (
              <div className="privacy-card">
                <div className="privacy-content auto-leave-select-row">
                  <div className="privacy-info">
                    <h4 className="privacy-title">{t("settings.autoLeave.unpaidLeaveType")}</h4>
                    <p className="privacy-description">
                      {t("settings.autoLeave.unpaidLeaveTypeDescription")}
                    </p>
                  </div>
                  <select
                    className="auto-leave-select"
                    disabled={!settings.isEnabled}
                    value={settings.unpaidLeaveTypeId ?? ""}
                    onChange={(e) =>
                      setField("unpaidLeaveTypeId", e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">{t("settings.autoLeave.selectLeaveType")}</option>
                    {activeLeaveTypes.map((lt) => (
                      <option key={lt.id} value={lt.id}>
                        {lt.name}
                        {lt.deducted === false ? ` (${t("settings.autoLeave.unpaidLabel")})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            <Message info className="auto-leave-payroll-note">
              <Message.Header>{t("settings.autoLeave.payrollTitle")}</Message.Header>
              <p>{t("settings.autoLeave.payrollDescription")}</p>
            </Message>
          </div>

          <div className={`auto-leave-section ${!settings.isEnabled ? "auto-leave-section--muted" : ""}`}>
            <h4 className="auto-leave-section-title">{t("settings.autoLeave.approvalTitle")}</h4>
            <ToggleRow
              title={t("settings.autoLeave.autoApprove")}
              description={t("settings.autoLeave.autoApproveDescription")}
              checked={settings.autoApprove}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("autoApprove", v)}
            />
            <ToggleRow
              title={t("settings.autoLeave.notifyEmployee")}
              description={t("settings.autoLeave.notifyEmployeeDescription")}
              checked={settings.notifyEmployee}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("notifyEmployee", v)}
            />
            <ToggleRow
              title={t("settings.autoLeave.notifyManager")}
              description={t("settings.autoLeave.notifyManagerDescription")}
              checked={settings.notifyManager}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("notifyManager", v)}
            />
          </div>

          <div className={`auto-leave-section ${!settings.isEnabled ? "auto-leave-section--muted" : ""}`}>
            <h4 className="auto-leave-section-title">{t("settings.autoLeave.guardsTitle")}</h4>
            <ToggleRow
              title={t("settings.autoLeave.skipHolidays")}
              description={t("settings.autoLeave.skipHolidaysDescription")}
              checked={settings.skipHolidaysAndNonWorkingDays}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("skipHolidaysAndNonWorkingDays", v)}
            />
            <ToggleRow
              title={t("settings.autoLeave.skipExistingLeave")}
              description={t("settings.autoLeave.skipExistingLeaveDescription")}
              checked={settings.skipIfAlreadyOnLeave}
              disabled={!settings.isEnabled}
              onChange={(v) => setField("skipIfAlreadyOnLeave", v)}
            />
          </div>

        </>
      )}
      </div>
    </div>
  );
};

export default AutoLeaveDeductionSettings;
